import React from 'react';
import { Box, styled } from '@mui/material';
import { PickersDay } from '@mui/x-date-pickers';
import { WorkoutsByDate } from '../../types';
import { getDateKey } from '../../utils';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ScheduleIcon from '@mui/icons-material/Schedule';

// Custom styled PickersDay with square shape and borders
const StyledPickersDay = styled(PickersDay)(({ theme }) => ({
  borderRadius: 4,
  border: `1px solid ${theme.palette.divider}`,
  width: 40,
  height: 40,
  margin: 2,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingTop: 4,
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    border: `1px solid ${theme.palette.primary.main}`,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  }
}));

interface CustomDayProps {
  day: Date;
  workouts: WorkoutsByDate;
  workoutDays: Set<string>;
  bodyWeightDays: Set<string>;
  programDays: Set<string>;
  inProgressDate?: string | null;
  // Include other props from PickersDayProps
  outsideCurrentMonth: boolean;
  isFirstVisibleCell: boolean;
  isLastVisibleCell: boolean;
  selected: boolean;
  onDaySelect: (day: Date) => void;
}

export const CustomDay: React.FC<CustomDayProps> = (props) => {
  const { day, workouts, workoutDays, bodyWeightDays, programDays, inProgressDate, selected, ...other } = props;
  const key = getDateKey(day);
  const hasWorkouts = workoutDays.has(key);
  const hasBodyWeight = bodyWeightDays.has(key);
  const hasProgramSession = programDays.has(key);
  const hasActiveWorkout = inProgressDate === key;
  
  return (
    <Box position="relative" width={1}>
      <StyledPickersDay day={day} selected={selected} {...other} />
      <Box 
        position="absolute" 
        sx={{
          display: 'flex',
          flexDirection: 'row',
          bottom: 6,
          left: '50%',
          transform: 'translateX(-50%)',
          gap: 0.5
        }}
      >
        {hasWorkouts && (
          <FitnessCenterIcon 
            sx={{
              color: '#4caf50',
              fontSize: 12
            }}
          />
        )}
        {hasBodyWeight && (
          <MonitorWeightIcon
            sx={{
              color: 'secondary.main',
              fontSize: 12
            }}
          />
        )}
        {hasProgramSession && (
          <EmojiEventsIcon sx={{ color: 'primary.main', fontSize: 12 }} />
        )}
        {hasActiveWorkout && (
          <ScheduleIcon sx={{ color: 'warning.main', fontSize: 12 }} />
        )}
      </Box>
    </Box>
  );
}; 